import React, { useState } from "react";
import {
  MultiColumnScheduleConfigData,
  MultiDayScheduleConfigData,
} from "../providers/ConfigProvider";
import { View, Text, StyleSheet } from "react-native";
import { PaginatedEvents } from "./PaginatedEvents";
import { ThemeOptions, useTheme } from "../providers/ThemeProvider";
import dayjs from "dayjs";
import { useDateFormatter } from "../hooks/useDateFormatter";
import { findIndex, max } from "lodash";
import { BorderedContainer } from "./BorderedContainer";
import { PageProgressChip } from "./PageProgressChip";
import { config } from "../config";
import { TimeZone } from "../../types";
import { ScheduleEvent } from "../providers/ConfigProvider";

const makeStyles = (theme: ThemeOptions, isPortrait: boolean) => {
  return StyleSheet.create({
    mainContent: {
      flex: 1,
      flexDirection: isPortrait ? "column" : "row",
      paddingVertical: theme.spacing.medium,
      paddingHorizontal: theme.spacing.regular,
    },
    dayHeader: {
      height: theme.scaled(60),
      paddingHorizontal: theme.spacing.medium,
      alignItems: "center",
      justifyContent: "center",
      borderBottomWidth: theme.borderWidth.regular,
      borderColor: theme.toRgba(theme.contrastColor, 0.2),
    },
    dayHeaderChip: {
      position: "absolute",
      right: theme.spacing.medium,
      top: 0,
      bottom: 0,
      justifyContent: "center",
    },
    dayTitle: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.large,
      fontWeight: "500",
    },
    columnHeader: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: theme.spacing.medium,
      paddingVertical: theme.spacing.small,
      borderLeftWidth: theme.borderWidth.large,
      borderColor: "transparent",
    },
    columnLabel: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.body,
      opacity: 0.5,
      letterSpacing: 1,
    },
    columnTime: { width: theme.scaled(110) },
    columnEvent: { flex: 1, paddingRight: theme.spacing.medium },
    columnLocation: { textAlign: "right" },
  });
};

export const MultiColumnSchedule = ({
  data,
  onLoopComplete,
  orientation = "landscape",
}: {
  data: MultiDayScheduleConfigData | MultiColumnScheduleConfigData;
  onLoopComplete?: () => void;
  orientation?: "portrait" | "landscape";
}) => {
  const theme = useTheme();
  const isPortrait = orientation === "portrait";
  const styles = makeStyles(theme, isPortrait);
  const formatter = useDateFormatter();

  const daysEventCount = (data?.events ?? []).map((d) => d.events.length);
  const maxEvents = max(daysEventCount) || 0;
  const maxEventsIndex = findIndex(daysEventCount, (c) => c === maxEvents);

  return (
    <View style={styles.mainContent}>
      {data.events.map((day, index) => {
        const title =
          day.type === "MultiDaySchedule"
            ? formatter.dateDayMonth(dayjs(day.date).toDate())
            : day.title;
        const timeZone =
          day.type === "MultiDaySchedule"
            ? (data as MultiDayScheduleConfigData).timeZone
            : day.timeZone;
        const disableLoop =
          day.type === "MultiDaySchedule"
            ? (data as MultiDayScheduleConfigData).disableLoop
            : day.disableLoop;
        const nextDayEvents =
          day.type === "MultiColumnSchedule" ? day.nextDayEvents : undefined;
        return (
          <DayBlock
            key={index}
            index={index}
            lastIndex={data.events.length - 1}
            isPortrait={isPortrait}
            title={title}
            events={day.events}
            timeZone={timeZone}
            disableLoop={disableLoop}
            nextDayEvents={nextDayEvents}
            onLoopComplete={() => {
              if (index === maxEventsIndex) {
                onLoopComplete?.();
              }
            }}
            styles={styles}
          />
        );
      })}
    </View>
  );
};

type DayBlockProps = {
  index: number;
  lastIndex: number;
  isPortrait: boolean;
  title: string;
  events: ScheduleEvent[];
  timeZone?: TimeZone;
  disableLoop?: boolean;
  nextDayEvents?: ScheduleEvent[];
  onLoopComplete: () => void;
  styles: ReturnType<typeof makeStyles>;
};

const DayBlock = ({
  index,
  lastIndex,
  isPortrait,
  title,
  events,
  timeZone,
  disableLoop,
  nextDayEvents,
  onLoopComplete,
  styles,
}: DayBlockProps) => {
  const theme = useTheme();
  const [pageInfo, setPageInfo] = useState({ currentIndex: 0, total: 1 });

  return (
    <BorderedContainer
      variant={index % 2 === 0 ? "default" : "secondary"}
      style={{
        flex: !isPortrait ? 1 : undefined,
        marginRight:
          !isPortrait && index !== lastIndex ? theme.spacing.small : 0,
        marginBottom:
          isPortrait && index !== lastIndex ? theme.spacing.small : 0,
      }}
    >
      <View style={styles.dayHeader}>
        <Text style={styles.dayTitle} numberOfLines={1}>
          {title}
        </Text>
        {isPortrait && pageInfo.total > 1 && (
          <View style={styles.dayHeaderChip}>
            <PageProgressChip
              currentIndex={pageInfo.currentIndex}
              total={pageInfo.total}
              durationMs={config.eventsPaginated.changePageRate}
            />
          </View>
        )}
      </View>
      {isPortrait && (
        <View style={styles.columnHeader}>
          <Text style={[styles.columnLabel, styles.columnTime]}>START</Text>
          <Text style={[styles.columnLabel, styles.columnTime]}>END</Text>
          <Text style={[styles.columnLabel, styles.columnEvent]}>EVENT</Text>
          <Text style={[styles.columnLabel, styles.columnLocation]}>
            LOCATION
          </Text>
        </View>
      )}
      <PaginatedEvents
        events={events}
        isVertical={isPortrait}
        nextDayEvents={nextDayEvents}
        timeZone={timeZone}
        onLoopComplete={onLoopComplete}
        disableLoop={disableLoop}
        onPageChange={(currentIndex, total) =>
          setPageInfo({ currentIndex, total })
        }
      />
    </BorderedContainer>
  );
};
